import type { TerminalCredentialState } from "./terminal-state";

type TerminalTrustRecord = {
  key: string;
  value: TerminalCredentialState;
  updatedAt: string;
};

const terminalTrustDbName = "more-pos-terminal-trust-v1";
const terminalTrustDbVersion = 1;
const terminalTrustStoreName = "credentials";
const currentTerminalKey = "current";

function validState(value: unknown): TerminalCredentialState | null {
  if (!value || typeof value !== "object") return null;
  const candidate = value as Partial<TerminalCredentialState>;
  if (typeof candidate.deviceSecret !== "string" || !candidate.deviceSecret) return null;

  return {
    deviceSecret: candidate.deviceSecret,
    identity: candidate.identity ?? null,
    verifiedAt: typeof candidate.verifiedAt === "string" ? candidate.verifiedAt : null,
  };
}

function requestValue<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error("TERMINAL_TRUST_IDB_REQUEST_FAILED"));
  });
}

function transactionDone(transaction: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    transaction.oncomplete = () => resolve();
    transaction.onabort = () => reject(transaction.error ?? new Error("TERMINAL_TRUST_IDB_TRANSACTION_ABORTED"));
    transaction.onerror = () => reject(transaction.error ?? new Error("TERMINAL_TRUST_IDB_TRANSACTION_FAILED"));
  });
}

function openTerminalTrustDb(): Promise<IDBDatabase> {
  if (typeof indexedDB === "undefined") {
    return Promise.reject(new Error("TERMINAL_TRUST_INDEXEDDB_UNAVAILABLE"));
  }

  return new Promise((resolve, reject) => {
    const request = indexedDB.open(terminalTrustDbName, terminalTrustDbVersion);

    request.onupgradeneeded = () => {
      const db = request.result;
      const transaction = request.transaction;
      let store: IDBObjectStore;

      if (!db.objectStoreNames.contains(terminalTrustStoreName)) {
        store = db.createObjectStore(terminalTrustStoreName, { keyPath: "key" });
      } else if (transaction) {
        store = transaction.objectStore(terminalTrustStoreName);
      } else {
        return;
      }

      if (!store.indexNames.contains("updatedAt")) {
        store.createIndex("updatedAt", "updatedAt", { unique: false });
      }
    };

    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error("TERMINAL_TRUST_IDB_OPEN_FAILED"));
    request.onblocked = () => reject(new Error("TERMINAL_TRUST_IDB_OPEN_BLOCKED"));
  });
}

export async function readDedicatedTerminalCredential(): Promise<TerminalCredentialState | null> {
  let db: IDBDatabase | null = null;

  try {
    db = await openTerminalTrustDb();
    const transaction = db.transaction(terminalTrustStoreName, "readonly");
    const done = transactionDone(transaction);
    const record = await requestValue<TerminalTrustRecord | undefined>(
      transaction.objectStore(terminalTrustStoreName).get(currentTerminalKey),
    );
    await done;
    return validState(record?.value);
  } catch {
    // The legacy runtimeMeta copy remains a compatibility fallback. A failure in
    // this dedicated trust store must never make an existing terminal unusable.
    return null;
  } finally {
    db?.close();
  }
}

export async function writeDedicatedTerminalCredential(state: TerminalCredentialState) {
  let db: IDBDatabase | null = null;

  try {
    db = await openTerminalTrustDb();
    const transaction = db.transaction(terminalTrustStoreName, "readwrite");
    const done = transactionDone(transaction);
    await requestValue(
      transaction.objectStore(terminalTrustStoreName).put({
        key: currentTerminalKey,
        value: state,
        updatedAt: new Date().toISOString(),
      } satisfies TerminalTrustRecord),
    );
    await done;
    return true;
  } catch {
    return false;
  } finally {
    db?.close();
  }
}
